import { useEffect, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import CinHeader from '@/components/cinematic/CinHeader';
import CinFooter from '@/components/cinematic/CinFooter';
// MobileCartFab removed 2026-05-24 — redundant: CinHeader cart icon now visible on all viewport sizes.
import WhatsAppFloat from '@/components/WhatsAppFloat';
import ScrollProgress from '@/components/ScrollProgress';
import HubChatbot from '@/components/HubChatbot';
import BackToTop from './BackToTop';
import MobileActionBar from './MobileActionBar';
import { useCinReveal, useCinMagnetic, useCinPressTilt } from '@/hooks/useCinReveal';
import { consumeHandoffScrollSkip } from '@/lib/staticHandoff';

interface Props {
  children: ReactNode;
  noChat?: boolean;
}

export default function PremiumLayout({ children, noChat }: Props) {
  const { pathname } = useLocation();

  // Cinematic scroll-reveal + magnetic-button observers, re-bind on route change.
  useCinReveal(pathname);
  useCinMagnetic(pathname);
  useCinPressTilt();

  // Scroll to top on route change — except when taking over a prerendered
  // page the visitor may already have scrolled (src/lib/staticHandoff.ts).
  // Checked in the effect, not during render: a render React discards (it
  // does on lazy routes) must not use up the one-shot.
  useEffect(() => {
    if (consumeHandoffScrollSkip(pathname)) return;
    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
  }, [pathname]);

  return (
    <div className="min-h-screen bg-white text-gray-900" dir="rtl">
      <ScrollProgress />
      <CinHeader />
      {/* Plain <main> since 2026-10-07: its framer fade repeated the one
          PageTransition already runs on every navigation, and was the only
          reason framer-motion sat in the first page load. */}
      <main id="main-content" key={pathname}>
        {children}
      </main>
      <CinFooter />
      {/* Room under the footer for the phone action bar. */}
      <div aria-hidden="true" className="h-[calc(68px+env(safe-area-inset-bottom,0px))] md:hidden" />
      <MobileActionBar />
      <BackToTop />
      {!noChat && <HubChatbot />}
      <WhatsAppFloat />
    </div>
  );
}
